<template>
    <div v-if="visible" class="live-dock">
        <div class="live-dock-status">
            <span class="live-dock-badge" :class="{ 'is-paused': paused }">
                <span class="live-dock-dot"></span>{{ paused ? 'En pausa' : 'En directo' }}
            </span>
            <div class="live-dock-where">
                <div class="live-dock-place">
                    <router-link v-if="stage && season" :to="{ name: 'StageRundown', params: { season_id: season.id, stage_id: stage.id } }">{{ season.name }} · {{ stage.name }}</router-link>
                    <span v-else>{{ season ? season.name : 'Evento' }}</span>
                    <span v-if="playback" class="live-dock-progress">{{ progress_label }}</span>
                </div>
                <div class="live-dock-step">
                    <b>{{ step.label }}</b><span v-if="step.detail" class="live-dock-detail">{{ step.detail }}</span>
                </div>
            </div>
        </div>

        <div class="live-dock-metrics">
            <div v-if="countdown != null" class="live-dock-metric" title="Tiempo restante">
                <i class="bi bi-stopwatch"></i><span class="live-dock-num">{{ countdown }}</span>
            </div>
            <div v-if="answers" class="live-dock-metric" :title="answers.label">
                <i class="bi" :class="answers.icon"></i><span class="live-dock-num">{{ answers.done }}/{{ answers.total }}</span>
            </div>
        </div>

        <PlaybackControls variant="dark" class="live-dock-controls" />
    </div>
</template>

<script>
import utils from '@/../../shared/utils'
import playbackState from '@/components/admin/playback-state'
import PlaybackControls from '@/components/admin/PlaybackControls.vue'

// "What's live right now" for the event room this admin is attached to -
// shown at the bottom of every admin screen while anything runs, so the
// person driving the event never has to go looking for the controls.
export default {
    name: 'LiveDock',
    components: { PlaybackControls },
    mixins: [playbackState],
    data(){
        return { now: Date.now(), timer: null };
    },
    computed: {
        visible(){ return this.is_live; },
        paused(){ return this.can_resume; },
        season(){
            return (this.game.seasons || []).find((s) => s.id == this.game.active_season_id) || null;
        },
        stage(){
            const stage_id = this.playback ? this.playback.stage_id : this.question && this.question.stage_id;
            return (this.game.stages || []).find((s) => s.id == stage_id) || null;
        },
        progress_label(){
            const total = this.playback.question_ids.length;
            return 'Actividad ' + Math.min(this.playback.index + 1, total) + ' de ' + total;
        },
        step(){
            const S = utils.GAME_STATES;
            const title = this.question ? this.question.title : '';
            switch(this.game.state){
                case S.INTERLUDE:
                    return { label: this.game.interlude && this.game.interlude.kind == 'intro' ? 'Animación de entrada' : 'Animación de llegada a parada' };
                case S.TITLE:
                    return { label: this.question && this.question.start_time ? 'Cuenta atrás' : 'Mostrando título', detail: title };
                case S.CHOICES:
                    if(this.media_waiting) return { label: this.game.media_sync.audience == utils.MEDIA_DISPLAY.SCREEN ? 'Cargando vídeo en la pantalla grande' : 'Cargando vídeo en los dispositivos', detail: title };
                    return { label: utils.is_content_activity(this.question && this.question.type) ? 'Mostrando contenido' : 'Respondiendo', detail: title };
                case S.RESULT:
                    return { label: this.game.feedback_stats ? 'Feedback' : 'Resultado', detail: title };
                case S.RANKING:
                    return { label: 'Clasificación', detail: this.game.active_ranking && this.game.active_ranking.name };
                case S.PAUSED:
                    return { label: 'En parada', detail: (this.game.paused_sector_name || '') + ' · pulsa Reanudar para seguir' };
                case S.END:
                    return { label: 'Pantalla final' };
                case S.STOPPED:
                    return { label: 'Detenido' };
            }
            return { label: this.playback && !this.playback.active ? 'Reproducción en pausa' : 'Preparando' };
        },
        media_waiting(){
            const sync = this.game.media_sync;
            return !!(sync && this.question && sync.question_id == this.question.id && !sync.play_at);
        },
        countdown(){
            if(this.game.state != utils.GAME_STATES.CHOICES || !this.question || !this.question.end_time)
                return null;
            const left = Math.max(0, Math.ceil((this.question.end_time - this.now) / 1000));
            return Math.floor(left / 60) + ':' + String(left % 60).padStart(2, '0');
        },
        // Answers (or "seen", for content) from the players connected right now.
        answers(){
            if(this.media_waiting){
                const sync = this.game.media_sync;
                const screen = sync.audience == utils.MEDIA_DISPLAY.SCREEN;
                return { done: sync.ready.length, total: sync.total, icon: screen ? 'bi-display' : 'bi-cloud-download', label: screen ? 'Pantallas grandes con el vídeo cargado' : 'Dispositivos con el vídeo cargado' };
            }
            if(![utils.GAME_STATES.CHOICES, utils.GAME_STATES.RESULT].includes(this.game.state) || !this.question)
                return null;
            const players = (this.game.clients || []).filter((c) => c.role == utils.USER_ROLES.PLAYER);
            const done = players.filter((p) => (p.data || []).some((d) => d.question == this.question.id)).length;
            const content = utils.is_content_activity(this.question.type);
            return { done, total: players.length, icon: content ? 'bi-eye' : 'bi-check2-square', label: content ? 'Equipos que lo han visto' : 'Equipos que han respondido' };
        }
    },
    watch: {
        visible: { immediate: true, handler(){ this.sync_timer(); } },
    },
    beforeUnmount(){
        clearInterval(this.timer);
    },
    methods: {
        sync_timer(){
            clearInterval(this.timer);
            this.timer = this.visible ? setInterval(() => { this.now = Date.now(); }, 500) : null;
        }
    }
}
</script>

<style lang="scss" scoped>
.live-dock{
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 10px 16px 10px 20px;
    background: var(--a-ink);
    color: #fff;
    border-top: 3px solid var(--a-live);
    box-shadow: 0 -6px 18px rgba(23, 32, 43, 0.12);
}
.live-dock-status{
    display: flex;
    align-items: center;
    gap: 14px;
    flex: 1;
    min-width: 0;
}
.live-dock-badge{
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 4px 10px;
    border-radius: 999px;
    background: var(--a-live);
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    flex-shrink: 0;
    &.is-paused{ background: #8a6a1f; }
}
.live-dock-dot{
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #fff;
    animation: dock-blink 1.4s ease-in-out infinite;
}
.is-paused .live-dock-dot{ animation: none; }
@keyframes dock-blink{ 50%{ opacity: 0.3; } }
.live-dock-where{ min-width: 0; line-height: 1.3; }
.live-dock-place{
    display: flex;
    gap: 10px;
    font-size: 0.78rem;
    color: rgba(255, 255, 255, 0.6);
    white-space: nowrap;
    a{ color: rgba(255, 255, 255, 0.75); text-decoration: none; overflow: hidden; text-overflow: ellipsis; &:hover{ color: #fff; text-decoration: underline; } }
}
.live-dock-progress{ color: rgba(255, 255, 255, 0.5); }
.live-dock-step{
    font-size: 0.95rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.live-dock-detail{
    margin-left: 8px;
    color: rgba(255, 255, 255, 0.7);
}
.live-dock-metrics{ display: flex; gap: 18px; }
.live-dock-metric{
    display: flex;
    align-items: center;
    gap: 6px;
    color: rgba(255, 255, 255, 0.65);
}
.live-dock-num{
    font-family: "Archivo", sans-serif;
    font-weight: 800;
    font-size: 1.15rem;
    color: #fff;
    font-variant-numeric: tabular-nums;
}
@media (max-width: 991.98px){
    .live-dock{ flex-wrap: wrap; gap: 10px 16px; }
    .live-dock-status{ flex-basis: 100%; }
    .live-dock-controls{ margin-left: auto; }
}
</style>
